import * as THREE from 'three';
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit';
import { ImportedModelInstanceSlotMask } from '../appearance';
import { ImportedModelSelectionGeometryTemplate } from '../../interaction/selection-rendering';

/**
 * 一个正式 Unit 的稳定选择批次；整模型选择只维护一份 GPU 对象和固定容量矩阵缓冲。
 * 选择变化仅更新独占槽遮罩。矩阵仍由正式 Unit 的变化槽投影，原几何/实例对象均不修改。
 */
export class ImportedModelInstancedSelectionBatch extends THREE.InstancedMesh {
    /** 与既有选择派生对象使用同一诊断和导出过滤标志。 */
    public readonly isImportedModelInstancedSelectionOverlay = true;
    /** 区分稳定批次与命令态单槽投影。 */
    public readonly isImportedModelInstancedSelectionBatch = true;
    /** 正式 source 到固定槽的索引，不随当前选择顺序重排。 */
    private readonly indexBySource = new Map<THREE.Mesh, number>();
    /** 当前绘制的槽位，只在选择变化时更新。 */
    private readonly selectedSlots = new Set<number>();
    /** 独占选择遮罩及节点材质。 */
    private readonly slotMask: ImportedModelInstanceSlotMask;
    /** 只有 primitive 子集才拥有独占几何，完整 Unit 始终借用正式 geometry。 */
    private readonly geometryTemplate = new ImportedModelSelectionGeometryTemplate();
    /** 与 Unit 生命周期配对的退订函数。 */
    private unsubscribeUnit: (() => void) | null = null;
    /** 幂等释放标志，供缓存管理器剔除已失效批次。 */
    private disposed = false;

    /**
     * 创建固定容量的选择批次。
     * @param unit 提供正式几何、槽位及矩阵更新的 Unit。
     * @param selectedMaterial 共享选中配方，批次不拥有该模板。
     * @param primitiveIndex -1 表示完整几何，其它值表示局部 group。
     */
    public constructor(
        private readonly unit: ImportedModelInstancedMeshUnit,
        selectedMaterial: THREE.Material,
        private readonly primitiveIndex = -1,
    ) {
        const count = unit.getSnapshot().getSize();
        const mask = new ImportedModelInstanceSlotMask(count, false);
        let material: THREE.Material;
        try {
            material = mask.resolveMaterial(selectedMaterial);
        } catch (error) {
            mask.dispose();
            throw error;
        }
        super(unit.object.geometry, material, count);
        this.slotMask = mask;
        this.name = `imported-instanced-selection-batch:${unit.object.uuid}:${primitiveIndex}`;
        this.matrixAutoUpdate = false;
        this.frustumCulled = false;
        this.castShadow = false;
        this.receiveShadow = false;
        this.visible = false;
        this.userData.skipSelection = true;
        this.userData.skipSelectionSubtree = true;
        this.userData.isImportedModelOverlay = true;
        try {
            // 所有固定槽只绑定一次；未选槽也能跟随正式位姿，下一次选中无需重新复制全批次。
            unit.listSourceMeshes().forEach((source, index) => {
                this.indexBySource.set(source, index);
                if (!unit.attachInstanceMatrixProjection(source, this, undefined, index)) {
                    throw new Error(`选择批次无法绑定正式实例槽：${source.uuid}`);
                }
            });
            this.syncRenderProperties();
            this.unsubscribeUnit = unit.onDispose(() => this.dispose());
        } catch (error) {
            this.dispose();
            throw error;
        }
    }

    /** @returns 当前真正选中的槽位数，而不是批次固定容量。 */
    public get selectedCount(): number {
        return this.selectedSlots.size;
    }

    /** @returns 批次是否已经结束生命周期，外部不能重置此状态。 */
    public get isDisposed(): boolean {
        return this.disposed;
    }

    /** @param source 正式来源。 @returns 该来源当前是否在本批次中高亮。 */
    public isSourceSelected(source: THREE.Mesh): boolean {
        const index = this.indexBySource.get(source);
        return index !== undefined && this.selectedSlots.has(index);
    }

    /**
     * 发布本轮选择差异；不替换 Mesh、材质、矩阵缓冲或纹理对象。
     * @param sources 属于本 Unit 且需要本几何范围的 source 集合。
     */
    public setSelection(sources: ReadonlySet<THREE.Mesh>): void {
        if (this.isDisposed) throw new Error(`选择批次已释放：${this.name}`);
        const next = new Set<number>();
        for (const source of sources) {
            const index = this.indexBySource.get(source);
            if (index === undefined) throw new Error(`选择批次来源不属于正式 Unit：${source.uuid}`);
            next.add(index);
        }
        for (const index of this.selectedSlots) {
            if (!next.has(index)) {
                this.slotMask.setVisible(index, false);
                this.selectedSlots.delete(index);
            }
        }
        for (const index of next) {
            if (!this.selectedSlots.has(index)) {
                this.slotMask.setVisible(index, true);
                this.selectedSlots.add(index);
            }
        }
        this.syncRenderProperties();
        this.visible = this.selectedSlots.size > 0;
    }

    /** 清空选中槽但保留 UUID/材质/NodeBuilder 缓存，重新选择不再重新编译。 */
    public clearSelection(): void {
        for (const index of this.selectedSlots) this.slotMask.setVisible(index, false);
        this.selectedSlots.clear();
        this.visible = false;
    }

    /** 正式布局原位变化时更新几何引用；不把临时透明材质复制到选择层。 */
    private syncRenderProperties(): void {
        this.geometry = this.geometryTemplate.resolve(
            this.unit.object.geometry,
            this.primitiveIndex < 0 ? undefined : [this.primitiveIndex],
        );
        // 子集 geometry 使用 group 绘制，完整几何保持原有一次整体绘制语义。
        if (this.geometry !== this.unit.object.geometry && !Array.isArray(this.material))
            this.material = [this.material];
        this.renderOrder = Math.max(this.unit.object.renderOrder, 5);
        this.layers.mask = this.unit.object.layers.mask;
    }

    /** @param _raycaster 拾取器。 @param _intersects 命中集合；派生选择批次不参与业务拾取。 */
    public override raycast(_raycaster: THREE.Raycaster, _intersects: THREE.Intersection[]): void {}

    /** 释放独占缓冲、遮罩和材质，外部正式几何及矩阵不受影响。 */
    public override dispose(): void {
        if (this.isDisposed) return;
        this.disposed = true;
        this.unsubscribeUnit?.();
        this.unsubscribeUnit = null;
        for (const source of this.indexBySource.keys())
            this.unit.detachInstanceMatrixProjection(source, this);
        this.indexBySource.clear();
        this.selectedSlots.clear();
        this.visible = false;
        this.removeFromParent();
        try {
            super.dispose();
        } finally {
            try {
                this.slotMask.dispose();
            } finally {
                this.geometryTemplate.dispose();
            }
        }
    }
}
